import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyOTP, forgotPassword, clearError, resetAuthStatus } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';

const VerifyOTP = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { loading, error, success } = useSelector((state) => state.auth);

  // Email passed from ForgotPassword or SignUp page
  const email = location.state?.email || '';
  const verificationType = location.state?.type || 'forgot_password';

  useEffect(() => {
    dispatch(resetAuthStatus());
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate, dispatch]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (success) {
      if (verificationType === 'forgot_password') {
        navigate('/reset-password', { state: { email, otp: otp.join('') } });
        dispatch(resetAuthStatus());
      } else {
        // For signup verification, we might redirect to a success page or login
        navigate('/login');
        dispatch(resetAuthStatus());
      }
    }
  }, [success, navigate, email, verificationType, otp, dispatch]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResend = () => {
    dispatch(forgotPassword({ email }));
    setTimer(30);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    inputRefs.current[0].focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    dispatch(verifyOTP({ email, otp: otpValue }));
  };

  const isComplete = otp.every(digit => digit !== '');

  return (
    <AuthLayout
      title="Verify Account"
      subtitle={`We've sent a 6-digit code to ${email}`}
    >
      {error && <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>{error}</Alert>}

      <Form onSubmit={handleSubmit} className="text-center pt-2">
        <div className="otp-inputs d-flex justify-content-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              className="text-center fw-semibold border rounded"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              autoFocus={index === 0}
              disabled={loading}
              style={{ width: "40px", height: "50px", fontSize: "1.25rem" }}
            />
          ))}
        </div>

        <div className="timer mb-4">
          {timer > 0 ? (
            <span className="d-flex align-items-center justify-content-center gap-2">
              <span className="text-muted"><i className="bi bi-clock"></i> Time Left -</span> 00:{timer < 10 ? `0${timer}` : timer}
            </span>
          ) : (
            <div className="text-center d-flex flex-column">
              <span className="text-muted small mb-1">Didn't receive code?</span>
              <span className="text-primary fw-semibold pointer" onClick={handleResend} style={{ cursor: canResend ? 'pointer' : 'not-allowed', opacity: canResend ? 1 : 0.5 }}>Resend OTP</span>
            </div>
          )}
        </div>

        <Button 
          variant="primary" 
          type="submit" 
          className="w-100 btn-custom d-flex align-items-center justify-content-center gap-2"
          disabled={!isComplete || loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Verifying...
            </>
          ) : 'Verify Code'}
        </Button>

        <div className="auth-footer">
          <Link to="/forgot-password" className="text-decoration-none d-flex align-items-center justify-content-center gap-2">
            <i className="bi bi-arrow-left"></i> Change email
          </Link>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default VerifyOTP;
